import { useEffect, useMemo, useState } from 'react'
import { Field, Form } from 'react-final-form'
import ControlInput from 'workflow/common/controlInput'
import { CAMPAIGN_NAME_LIMIT } from 'workflow/utils/helpers'

import Banner from 'common/components/banner'
import Button from 'common/components/button'
import Checkbox from 'common/components/checkbox'
import { FieldType } from 'common/components/dynamicFields/interfaces'
import FormItem from 'common/components/formItem'
import Input from 'common/components/input'
import LoadingOverlay from 'common/components/LoadingOverlay'
import Modal from 'common/components/Modal'
import { MissingDataInverted } from 'common/icons'
import SendDate from 'features/unifiedFlow/components/templateSelection/setupExperiment/SendDate'
import {
  parseWorkflowName,
  validateWorkflowName,
} from 'features/unifiedFlow/contentPage/helpers'

import {
  getElementModeLabel,
  getMultipleTemplatesBannerText,
} from './duplicateWorkflowModal.utils'

const MAX_HUMAN_CONTROL_LENGTH = 128

export type DuplicateWorkflowModalElement = {
  elementId: number
  displayName?: string
  elementType?: 'component' | 'template' | string
  optimisationType?: 'optimize' | 'personalize' | string
  templateName?: string
  projectId?: string
  projectName?: string
  ownSubjectLine?: string
  minBreakLines?: number
  maxBreakLines?: number
  sendDate?: string | null
}

export type HumanControlLineByElement = {
  elementId: number
  text: string
  sendDate?: string | null
}

export type DuplicateWorkflowModalValues = {
  name: string
  includeTemplateAndBrief: boolean
  includeGeneratedContent: boolean
  editHumanControlLine: boolean
  humanControlLines: HumanControlLineByElement[]
}

type Props = {
  isOpen: boolean
  initialName: string
  elements: DuplicateWorkflowModalElement[]
  isLoading?: boolean
  isPreparing?: boolean
  isPersonaliseWorkflow?: boolean
  canIncludeGeneratedContent?: boolean
  templatesWithoutGeneratedContent?: string[]
  allowHumanControlEdit?: boolean
  onClose: () => void
  onConfirm: (values: DuplicateWorkflowModalValues) => Promise<void> | void
}

const DuplicateWorkflowModal = ({
  isOpen,
  initialName,
  elements,
  isLoading,
  isPreparing = false,
  isPersonaliseWorkflow = false,
  canIncludeGeneratedContent = true,
  templatesWithoutGeneratedContent = [],
  allowHumanControlEdit = false,
  onClose,
  onConfirm,
}: Props) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [nameValidationError, setNameValidationError] = useState<string>()
  const [showMultipleTemplatesBanner, setShowMultipleTemplatesBanner] =
    useState(true)

  const handleSubmit = async (values: DuplicateWorkflowModalValues) => {
    setIsSubmitting(true)
    try {
      await onConfirm({
        ...values,
        editHumanControlLine: canEditHumanControl,
        // Personalise campaigns must never duplicate generated/optimised variants.
        includeGeneratedContent:
          isPersonaliseWorkflow || !canIncludeGeneratedContent
            ? false
            : (values.includeGeneratedContent ?? false),
      })
      onClose()
    } catch (error) {
      return {
        name: 'A workflow with this name already exists. Please choose another name.',
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const loading = isSubmitting || !!isLoading
  const controllableElements = useMemo(
    () =>
      elements.filter((e) => (e.elementType ?? 'component') === 'component'),
    [elements]
  )
  const hasMultipleElements = controllableElements.length > 1
  const hasAnyControllableElements = controllableElements.length > 0
  const canEditHumanControl =
    hasAnyControllableElements && allowHumanControlEdit

  const initialHumanControlLines = useMemo<HumanControlLineByElement[]>(
    () =>
      controllableElements.map((e) => ({
        elementId: e.elementId,
        text: e.ownSubjectLine ?? '',
        sendDate: null,
      })),
    [controllableElements]
  )

  useEffect(() => {
    if (!isOpen) {
      return
    }
    setShowMultipleTemplatesBanner(true)
  }, [isOpen, controllableElements.length])

  if (!isOpen) {
    return null
  }

  return (
    <Modal
      visible={true}
      centered
      closable={true}
      footer={null}
      onClose={onClose}
      data-cy="duplicate-workflow-modal"
      data-testid="duplicate-workflow-modal"
      style={{ minWidth: 560 }}
    >
      <div className="flex w-full">
        <h1 className="mt-2 text-2xl font-bold text-coolGray-800">
          Make a duplicate
        </h1>
      </div>

      <div className="relative mt-2">
        <LoadingOverlay
          isLoading={isPreparing}
          overlayType="local"
          phrases={['Loading component settings']}
        />
        <p className="mb-4 text-base font-medium text-coolGray-800">
          Choose which components to include below.
        </p>
        <Form<DuplicateWorkflowModalValues>
          onSubmit={handleSubmit}
          initialValues={{
            name: `Copy of ${initialName}`,
            includeTemplateAndBrief: true,
            includeGeneratedContent: false,
            editHumanControlLine: canEditHumanControl,
            humanControlLines: initialHumanControlLines,
          }}
          validate={(values) => {
            const errors: Partial<
              Record<keyof DuplicateWorkflowModalValues, string>
            > = {}

            errors.name = validateWorkflowName(values.name)

            const anyTooLong = values.humanControlLines?.some(
              (l) => (l?.text ?? '').length > MAX_HUMAN_CONTROL_LENGTH
            )
            if (anyTooLong) {
              errors.humanControlLines = `Control line should be up to ${MAX_HUMAN_CONTROL_LENGTH} characters`
            }

            return errors
          }}
          render={({ handleSubmit, hasValidationErrors, values }) => (
            <form onSubmit={handleSubmit} className="text-sm min-w-200 ">
              <div className="space-y-2">
                <Field<string>
                  name="name"
                  parse={(value) => {
                    const { value: parsedValue, validationError } =
                      parseWorkflowName(value)
                    setNameValidationError(validationError)
                    return parsedValue
                  }}
                  render={({ input, meta }) => (
                    <FormItem
                      label={
                        <span className="text-xs font-medium text-coolGray-800">
                          Workflow name
                        </span>
                      }
                      htmlFor="duplicate_workflow_name"
                      error={
                        meta.error ||
                        nameValidationError ||
                        (!meta.dirtySinceLastSubmit && meta.submitError) ||
                        undefined
                      }
                    >
                      <div className="relative">
                        <div className="absolute right-0 -top-6 text-coolGray-500 text-xs font-normal">
                          {input.value?.length || 0} / {CAMPAIGN_NAME_LIMIT}
                        </div>
                        <Input
                          type="text"
                          id="duplicate_workflow_name"
                          variant="default"
                          className="text-base font-normal bg-gold-40"
                          value={input.value}
                          name={input.name}
                          onChange={input.onChange}
                          disabled={loading}
                          maxLength={CAMPAIGN_NAME_LIMIT}
                        />
                      </div>
                    </FormItem>
                  )}
                />
              </div>

              <div className="mt-2 space-y-4">
                <div className="text-xs font-medium text-coolGray-800 mt-5">
                  Select what to include:
                </div>
                <Field<boolean> name="includeTemplateAndBrief">
                  {({ input }) => (
                    <div>
                      <Checkbox
                        isChecked={true}
                        onChange={(checked) => input.onChange(checked)}
                        isDisabled={true}
                        label={
                          <span className="flex flex-col leading-tight">
                            <span className="font-medium">
                              Brief configuration
                            </span>
                            <span className="text-xs text-base-700">
                              (Required) Use this when you want to start with
                              previous brief, but generate new content.
                            </span>
                          </span>
                        }
                      />
                    </div>
                  )}
                </Field>

                {!isPersonaliseWorkflow && (
                  <Field<boolean> name="includeGeneratedContent">
                    {({ input }) => (
                      <div>
                        <Checkbox
                          isChecked={Boolean(input.value)}
                          onChange={(checked) => input.onChange(checked)}
                          isDisabled={loading || !canIncludeGeneratedContent}
                          label={
                            <span className="flex flex-col leading-tight">
                              <span className="font-medium">
                                Generated content
                              </span>
                              <span className="text-xs text-base-700">
                                Use this when you want to reuse previous
                                content. You can still make changes.
                              </span>
                            </span>
                          }
                          labelClassName="font-normal"
                        />
                        {!canIncludeGeneratedContent && (
                          <p className="text-xs text-red-400 mt-2">
                            Content cannot be duplicated for template/s in use.
                          </p>
                        )}
                        {canIncludeGeneratedContent &&
                          templatesWithoutGeneratedContent.length > 0 && (
                            <p className="mt-2.5 text-xs text-red-400">
                              Content cannot be duplicated for templates:{' '}
                              {templatesWithoutGeneratedContent.join(', ')}
                            </p>
                          )}
                      </div>
                    )}
                  </Field>
                )}
              </div>

              {canEditHumanControl && (
                <div className="mt-6 space-y-6">
                  {hasMultipleElements && showMultipleTemplatesBanner && (
                    <Banner
                      type="info"
                      icon={
                        <MissingDataInverted
                          className="text-base-700"
                          isDefaultColor={false}
                        />
                      }
                      heading="REVIEW TEMPLATE SETTINGS"
                      text={getMultipleTemplatesBannerText(
                        controllableElements
                      )}
                    />
                  )}

                  {controllableElements.map((element, index) => (
                    <div
                      key={element.elementId}
                      className="border-t border-gold-200 pt-4"
                    >
                      <div className="flex items-center gap-2 text-xs font-medium tracking-wide text-coolGray-800 mb-2">
                        <span className="uppercase">{element.displayName}</span>
                        <span className="rounded-sm bg-gold-100 px-2 py-0.5 text-xs font-medium text-coolGray-800">
                          {getElementModeLabel(element)}
                        </span>
                        {element.templateName ? (
                          <span className="font-normal text-xs text-coolGray-600">
                            ({element.templateName})
                          </span>
                        ) : null}
                      </div>

                      <div className="flex items-start gap-6">
                        <div className="flex-1 max-w-150">
                          <Field<string>
                            name={`humanControlLines[${index}].text`}
                          >
                            {({ input }) => (
                              <FormItem
                                label={
                                  <span className="text-xs font-normal text-coolGray-800">
                                    Control {element.displayName}
                                  </span>
                                }
                                htmlFor={`duplicate_workflow_human_control_${element.elementId}`}
                              >
                                <div className="relative">
                                  <div className="absolute right-0 -top-6 text-coolGray-500 text-xs font-normal">
                                    {input.value?.length || 0} /{' '}
                                    {MAX_HUMAN_CONTROL_LENGTH}
                                  </div>
                                  <ControlInput
                                    data-cy={`duplicate_workflow_human_control_${element.elementId}`}
                                    data-testid={`duplicate_workflow_human_control_${element.elementId}`}
                                    id={`duplicate_workflow_human_control_${element.elementId}`}
                                    className="text-base font-normal text-coolGray-800 border-gold-400 bg-gold-40 pt-2.5"
                                    value={input.value}
                                    name={input.name}
                                    onChange={(value) => input.onChange(value)}
                                    autoSize={{
                                      minRows:
                                        (element.minBreakLines ?? 0) > 0
                                          ? (element.minBreakLines ?? 0) + 1
                                          : 1.334,
                                    }}
                                    maxRows={element.maxBreakLines ?? 0}
                                    disabled={loading}
                                    maxLength={MAX_HUMAN_CONTROL_LENGTH}
                                  />
                                  <p
                                    className="mt-2 text-base-700 leading-tight"
                                    style={{ fontSize: '11px' }}
                                  >
                                    Keep the same or enter a new control line.
                                  </p>
                                </div>
                              </FormItem>
                            )}
                          </Field>
                        </div>

                        {!isPersonaliseWorkflow && (
                          <div className="ml-auto w-38 flex-shrink-0">
                            <SendDate
                              name={`humanControlLines[${index}].sendDate`}
                              config={{
                                ordinal: index + 1,
                                name: 'iso_send_date',
                                label: 'Send date',
                                required: true,
                                type: FieldType.Date,
                              }}
                              labelClassName="text-xs font-normal text-coolGray-800"
                              isDisabled={loading}
                              data-testid={`duplicate-workflow-send-date-${element.elementId}`}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end mt-8">
                <Button
                  variant="link"
                  className="mr-4 text-base text-coolGray-400"
                  onClick={onClose}
                  data-cy="duplicate-workflow-modal-cancel-button"
                  data-testid="duplicate-workflow-modal-cancel-button"
                  disabled={loading}
                >
                  CANCEL
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={loading}
                  disabled={
                    loading || hasValidationErrors || !values.name?.trim()
                  }
                  data-cy="duplicate-workflow-modal-confirm-button"
                  data-testid="duplicate-workflow-modal-confirm-button"
                >
                  DUPLICATE
                </Button>
              </div>
            </form>
          )}
        />
      </div>
    </Modal>
  )
}

export default DuplicateWorkflowModal
